import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Play,
  User,
  MoreVertical,
  RefreshCw,
  Trash2,
  FolderInput,
  Tag,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Card, Button } from '@ai-tutor/ui';
import type { LessonError } from '@/lib/api-client';
import { describeFailure } from '@/lib/lesson-error';
import { hasOverview } from '@/lib/lesson-overview';

// Card-specific prop shape - decoupled from the LearningItem mock type so
// the card can be driven by either real backend data or mock data without
// dragging in mock-only fields (progress, lastAccessed, etc).
export interface LearningCardItem {
  id: string;
  title: string;
  description: string;
  tags: string[];
  isCreatedByUser?: boolean;
  instructor?: string;
  // Generation lifecycle (from backend ContentStatus). Drives the three
  // card states below - see the `phase` derivation in the component.
  status?: string;
  // Error payload from the failing task (only populated when status === 'failed').
  error?: LessonError | null;
}

interface LearningCardProps {
  item: LearningCardItem;
  currentVersion?: string;
  onContinue?: (item: LearningCardItem) => void;
  onRetry?: () => void;
  onDelete?: () => void;
  onMove?: () => void;
  onEditTags?: () => void;
}

export function LearningCard({
  item,
  currentVersion,
  onContinue,
  onRetry,
  onDelete,
  onMove,
  onEditTags,
}: LearningCardProps) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isFailed = item.status === 'failed';
  const isGenerating =
    item.status === 'pending' || item.status === 'processing';

  // Distinguishes a first-time generation (no overview yet) from a
  // regeneration of an already-populated lesson (we keep its existing content
  // visible). See `lib/lesson-overview`.
  const overviewLanded = hasOverview(item);

  // Three mutually-exclusive card states:
  //   generating - overview not produced yet → shimmer + "Creating lesson…"
  //   failed     - generation died before any overview → inline retry card
  //   ready      - normal content card (also covers a regenerating lesson
  //                that still has its previous overview, and a later-step
  //                failure where the overview survived - retry stays in menu)
  const phase: 'generating' | 'failed' | 'ready' =
    !overviewLanded && isFailed
      ? 'failed'
      : !overviewLanded && isGenerating
        ? 'generating'
        : 'ready';

  // Friendly, code-mapped failure copy. Cheap + pure, so computing it
  // unconditionally is fine; only read in the `failed` branch.
  const failure = describeFailure(item.error);

  const handleContinue = () => {
    navigate(`/my-learning/${item.id}`);
    onContinue?.(item);
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleRetryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    // Optimistic local spinner. The parent re-dispatches + refetches, which
    // flips status → processing and re-renders this card into its
    // generating state, so we never need to clear this flag ourselves.
    setIsRetrying(true);
    onRetry?.();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onDelete?.();
  };

  // Click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <Card
      className='group relative flex h-full cursor-pointer flex-col overflow-hidden transition-all duration-300 sm:hover:scale-[1.02]'
      onClick={handleContinue}
    >
      {/* Three-dot menu - shared across every state. Which items show is
          gated on `phase` (nothing to edit, and retry moves inline, until
          the overview exists). */}
      <div
        className='absolute right-2 top-2 z-10 sm:right-4 sm:top-4'
        ref={menuRef}
      >
        <button
          onClick={handleMenuToggle}
          // `tap-target`: drawn 28px, tapped as 44px - on a phone this is
          // the only route to Move / Edit / Delete.
          className='tap-target rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted'
          aria-label='Lesson options'
        >
          <MoreVertical className='h-4 w-4' />
        </button>

        {/* Dropdown menu */}
        {showMenu && (
          <div className='absolute right-0 top-full z-20 mt-2 w-48 rounded-lg border border-border bg-card py-1 shadow-lg'>
            {onMove && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onMove();
                }}
                className='flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted sm:py-2'
              >
                <FolderInput className='h-4 w-4' />
                Move to folder
              </button>
            )}
            {onEditTags && phase === 'ready' && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setShowMenu(false);
                  onEditTags();
                }}
                className='flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted sm:py-2'
              >
                <Tag className='h-4 w-4' />
                Edit details
              </button>
            )}
            {/* Later-step failure that kept its overview: retry lives in the
                menu so the content card stays clean. The overview-failed
                case (phase === 'failed') gets a prominent inline button
                instead. */}
            {isFailed && phase === 'ready' && (
              <button
                onClick={handleRetryClick}
                className='flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted sm:py-2'
              >
                <RefreshCw className='h-4 w-4' />
                Retry generation
              </button>
            )}
            <button
              onClick={handleDeleteClick}
              className='flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-destructive-emphasis transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 sm:py-2'
            >
              <Trash2 className='h-4 w-4' />
              Delete Lesson
            </button>
          </div>
        )}
      </div>

      {/* GENERATING - overview still being written. Shimmer placeholders
          share the exact treatment used by MyLearningPage's page-level
          LoadingSkeleton so a card resolving from refresh-skeleton →
          generating reads as one element gaining a status line, not a
          swap. */}
      {phase === 'generating' && (
        <div className='flex flex-1 flex-col p-4 sm:p-6'>
          <div className='mb-3 h-6 w-3/4 animate-pulse rounded-sm bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          <div className='mb-2 h-4 w-full animate-pulse rounded-sm bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          <div className='mb-5 h-4 w-2/3 animate-pulse rounded-sm bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          <div className='mt-auto flex items-center gap-2 text-xs font-medium text-muted-foreground'>
            <Loader2 className='h-3.5 w-3.5 animate-spin motion-reduce:animate-none' />
            <span>Creating lesson…</span>
          </div>
        </div>
      )}

      {/* FAILED before any overview - the lesson has nothing to show, so
          surface the error and a one-click retry as the primary action. */}
      {phase === 'failed' && (
        <div className='flex flex-1 flex-col p-4 sm:p-6'>
          <div className='mb-3 flex items-center gap-2.5 pr-8'>
            <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20'>
              <AlertCircle className='h-5 w-5 text-red-600 dark:text-red-400' />
            </div>
            <h2 className='text-base font-semibold leading-snug text-foreground'>
              Couldn&apos;t generate lesson
            </h2>
          </div>
          {/* Friendly, code-mapped copy (describeFailure). The raw provider
              message is intentionally not surfaced to the user. */}
          <p className='mb-5 line-clamp-2 text-sm leading-relaxed text-muted-foreground'>
            {failure.body}
          </p>
          <div className='mt-auto flex items-center justify-between'>
            {/* `destructive-emphasis`, not `destructive`: the default token is
                tuned for solid fills and measures 4.31:1 as 12px text on the
                card, just under AA. The emphasis ramp exists for exactly this
                (on-tint text) and clears 4.73:1, and carries its own dark
                value so no `dark:` override is needed. */}
            <span className='text-xs font-medium text-destructive-emphasis'>
              Generation failed
            </span>
            <Button
              variant='default'
              size='sm'
              onClick={handleRetryClick}
              disabled={isRetrying}
              className='tap-target px-4 py-2 text-xs font-medium'
            >
              {isRetrying ? (
                <Loader2 className='mr-1.5 h-3 w-3 animate-spin motion-reduce:animate-none' />
              ) : (
                <RefreshCw className='mr-1.5 h-3 w-3' />
              )}
              {isRetrying ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        </div>
      )}

      {/* READY - normal content card. */}
      {phase === 'ready' && (
        <div className='flex flex-1 flex-col p-4 sm:p-6'>
          {/* Title - full value stored server-side; clamp + tooltip for display. */}
          <h2
            title={item.title}
            className='mb-1.5 line-clamp-2 pr-8 text-base font-semibold leading-snug text-foreground sm:mb-3 sm:line-clamp-1 sm:text-xl'
          >
            {item.title}
          </h2>

          {/* Description */}
          <p className='mb-3 line-clamp-1 text-sm leading-relaxed text-muted-foreground sm:mb-5 sm:line-clamp-2'>
            {item.description}
          </p>

          {/* Tags */}
          {item.tags.length > 0 && (
            // Hidden on a phone: the row is for finding and opening a
            // lesson, and search already matches on tags.
            <div className='mb-6 hidden flex-wrap gap-1.5 sm:flex'>
              {item.tags.slice(0, 3).map(tag => (
                <span
                  key={tag}
                  className={`inline-flex items-center rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors`}
                >
                  {tag}
                </span>
              ))}
              {item.tags.length > 3 && (
                <span
                  className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground`}
                >
                  +{item.tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Footer */}
          <div className='mt-auto flex items-center justify-between'>
            <div
              className={`flex items-center gap-2 text-xs font-medium text-muted-foreground`}
            >
              {currentVersion && (
                <span
                  data-testid='learning-card-version'
                  className='inline-flex items-center rounded-md bg-muted px-2 py-0.5 font-mono text-xs font-medium text-muted-foreground'
                >
                  {/* The API sends `v1` on some paths and `1` on others; a
                      bare prefix rendered "vv1". */}
                  v{currentVersion.replace(/^v/i, '')}
                </span>
              )}
              {item.isCreatedByUser ? (
                <div className='flex items-center'>
                  <User className='mr-1.5 h-3 w-3' />
                  <span>Created by you</span>
                </div>
              ) : item.instructor ? (
                <span>by {item.instructor}</span>
              ) : null}
            </div>

            {/* The whole card opens the lesson; this is its named, focusable
                route. On a phone it shrinks to a chevron with the same name,
                so a screen reader still hears "Go to Lesson". */}
            <Button
              variant='default'
              size='sm'
              onClick={handleContinue}
              className='hidden px-4 py-2 text-xs font-medium transition-all duration-200 hover:scale-105 sm:inline-flex'
            >
              <Play className='mr-1.5 h-3 w-3' />
              Go to Lesson
            </Button>
            <button
              type='button'
              onClick={e => {
                e.stopPropagation();
                handleContinue();
              }}
              aria-label='Go to Lesson'
              className='tap-target -mr-1 rounded-md p-1 text-muted-foreground sm:hidden'
            >
              <ChevronRight className='h-5 w-5' />
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
